import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box } from '@mui/material';
import { MAX_SAVED_LAYOUTS, SavedLayout } from '../hooks/useLayoutStorage';
import { useLayoutStorage } from '../hooks/useLayoutStorage';
import { Dashboard } from '@shared/protos/v2/dashboard';

interface SaveLayoutDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (savedLayout: SavedLayout) => void;
  currentDashboard: Dashboard;
}

export const SaveLayoutDialog = ({ open, onClose, onSave, currentDashboard }: SaveLayoutDialogProps) => {
  const [layoutName, setLayoutName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const { getSavedLayoutLength, saveLayout } = useLayoutStorage();

  useEffect(() => {
    if (open) {
      setShowWarning(getSavedLayoutLength() >= MAX_SAVED_LAYOUTS);
    }
  }, [open, getSavedLayoutLength]);

  const handleSave = async () => {
    if (!layoutName.trim()) return;

    setIsSaving(true);
    try {
      const savedLayout = saveLayout(currentDashboard, layoutName.trim());
      onSave(savedLayout);
    } catch (error) {
      console.error('Error saving layout:', error);
    } finally {
      setLayoutName('');
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setLayoutName('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLayoutName(e.target.value);
  };

  const currentCount = getSavedLayoutLength();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Dashboard Layout</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save the current dashboard layout with all widget positions and configurations.
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Saved layouts: {currentCount}/{MAX_SAVED_LAYOUTS}
          </Typography>

          {showWarning && (
            <Typography variant="caption" color="warning.main" sx={{ mb: 2, fontStyle: 'italic' }}>
              Note: You have reached the maximum of 10 saved layouts. Saving a new layout will remove the oldest one.
            </Typography>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Layout Name"
            value={layoutName}
            onChange={handleChange}
            placeholder="Enter a name for this layout"
            variant="outlined"
            size="small"
            onKeyDown={handleKeyDown}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!layoutName.trim() || isSaving}>
          {isSaving ? 'Saving...' : 'Save Layout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
